import React, { useEffect, useMemo, useState } from 'react';
import { eachDayOfInterval, getDay, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import GroupedSelect from '../../../components/ui/GroupedSelect';
import { formatDate, formatDateWeekday } from '../../../utils/formatDate';
import { assignmentErrorMessage } from '../assignmentErrors';
import {
  findConflicts,
  listWorkerAssignments,
  createAssignments,
  updateAssignment,
  deleteAssignment,
  toMinutes,
  toTimeString,
  OVERLAP_ERROR_CODE,
} from '../../../services/workerAssignmentsService';

/** Les trois durees proposees. 'custom' revele une heure de fin saisie a la main. */
const DURATIONS = [
  { key: 'half', minutes: 4 * 60 },
  { key: 'full', minutes: 8 * 60 },
  { key: 'custom', minutes: null },
];

/** L M M J V S D — index date-fns : 0 = dimanche. */
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** « 07:00:00 » -> « 7:00 ». Pas de zero inutile devant l'heure. */
export const shortTime = (time) => {
  const m = toMinutes(time);
  if (m == null) return '';
  const hh = Math.floor(m / 60);
  const mm = String(m % 60)?.padStart(2, '0');
  return `${hh}:${mm}`;
};

/**
 * Une duree en minutes, dite comme on la dit sur un chantier :
 * 480 -> « 8 h », 510 -> « 8 h 30 ». Les minutes ne s'affichent que si elles
 * existent — « 8 h 00 » ne se dit pas.
 */
export const durationLabel = (minutes) => {
  if (minutes == null || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m)?.padStart(2, '0')}`;
};

/**
 * Modale d'assignation — un jour, ou une serie.
 *
 * Il n'y a plus de bloc am/pm/journee : une assignation est une heure de debut et
 * une heure de fin, toutes deux obligatoires. La duree n'est qu'une facon rapide
 * de calculer la fin.
 */
const AssignmentModal = ({
  isOpen,
  assignment,          // null = creation
  defaultDate,         // date pre-remplie au clic sur une case
  defaultStart,        // heure pre-remplie au clic dans la vue semaine
  workers,
  projects,
  trades,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const { t, language } = useTranslation();

  const isEdit = !!assignment?.id;

  const [workerId, setWorkerId] = useState(assignment?.workerId || '');
  const [projectId, setProjectId] = useState(assignment?.projectId || '');

  // ── Quand : un jour, ou une serie ──────────────────────────────────────────
  const [mode, setMode] = useState('single');            // 'single' | 'range'
  const [workDate, setWorkDate] = useState(assignment?.workDate || defaultDate || '');
  const [fromDate, setFromDate] = useState(assignment?.workDate || defaultDate || '');
  const [toDate, setToDate] = useState(assignment?.workDate || defaultDate || '');
  // La fin de semaine est exclue par defaut : un chantier ne roule pas le samedi.
  const [weekdays, setWeekdays] = useState({ 1: true, 2: true, 3: true, 4: true, 5: true, 6: false, 0: false });

  // ── Heures ─────────────────────────────────────────────────────────────────
  const [startTime, setStartTime] = useState(assignment?.startTime?.slice(0, 5) || defaultStart || '07:00');
  const [endTime, setEndTime] = useState(assignment?.endTime?.slice(0, 5) || '15:00');
  const [duration, setDuration] = useState(() => {
    const s = toMinutes(assignment?.startTime);
    const e = toMinutes(assignment?.endTime);
    if (s == null || e == null) return 'full';
    const d = e - s;
    return d === 240 ? 'half' : d === 480 ? 'full' : 'custom';
  });

  const [note, setNote] = useState(assignment?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState([]);
  const [result, setResult] = useState(null);   // ecran de resultat d'une serie

  const tradeName = (id) => trades?.find((tr) => tr?.id === id)?.name || '';

  /**
   * Ce qui s'affiche a droite du nom. Un surintendant n'a PAS de metier —
   * on retombe alors sur son role traduit, jamais sur un blanc.
   */
  const qualifier = (w) => tradeName(w?.trade_id) || t(`userManagement.roles.${w?.role}`) || '';

  const optionOf = (w) => ({
    value: w?.id,
    label: qualifier(w) ? `${w?.full_name || w?.email} — ${qualifier(w)}` : (w?.full_name || w?.email),
  });

  const workerGroups = useMemo(() => {
    const byRole = (role) => (workers || [])?.filter((w) => w?.role === role)?.map(optionOf);
    const groups = [];
    const ws = byRole('worker');
    const ss = byRole('supervisor');
    if (ws?.length) groups?.push({ label: t('teamSchedule.groups.workers'), options: ws });
    if (ss?.length) groups?.push({ label: t('teamSchedule.groups.supervisors'), options: ss });
    return groups;
  }, [workers, trades, t]);

  const projectOptions = useMemo(
    () => (projects || [])?.map((p) => ({
      value: p?.id,
      label: p?.projectNumber ? `${p?.projectNumber} · ${p?.name}` : p?.name,
      searchText: `${p?.projectNumber || ''} ${p?.name || ''}`?.toLowerCase(),
    })),
    [projects]
  );

  // ── La fin derive du debut, sauf en duree libre ─────────────────────────────
  const startMin = toMinutes(`${startTime}:00`);
  const computedEnd = useMemo(() => {
    if (duration === 'custom') return `${endTime}:00`;
    const preset = DURATIONS?.find((d) => d?.key === duration)?.minutes;
    if (startMin == null || preset == null) return null;
    return toTimeString(startMin + preset);
  }, [duration, startMin, endTime]);

  const endMin = toMinutes(computedEnd);
  const spanMinutes = startMin != null && endMin != null ? endMin - startMin : null;
  // Une plage qui passerait minuit est refusee ici, avant que la base ne la refuse.
  const timesValid = spanMinutes != null && spanMinutes > 0;

  // ── Les journees reellement visees ─────────────────────────────────────────
  const targetDates = useMemo(() => {
    if (mode === 'single') return workDate ? [workDate] : [];
    if (!fromDate || !toDate || toDate < fromDate) return [];
    try {
      return eachDayOfInterval({ start: parseISO(`${fromDate}T12:00:00`), end: parseISO(`${toDate}T12:00:00`) })
        ?.filter((d) => weekdays?.[getDay(d)])
        ?.map((d) => `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}-${String(d?.getDate())?.padStart(2, '0')}`);
    } catch {
      return [];
    }
  }, [mode, workDate, fromDate, toDate, weekdays]);

  // ── Les assignations existantes de cette personne, sur la plage visee ───────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerId || !targetDates?.length) { setExisting([]); return; }
      const rows = await listWorkerAssignments(workerId, targetDates?.[0], targetDates?.[targetDates.length - 1]);
      if (!cancelled) setExisting(rows || []);
    })();
    return () => { cancelled = true; };
  }, [workerId, targetDates?.[0], targetDates?.[targetDates?.length - 1]]);

  /** Les journees visees, triees en « libres » et « en conflit ». */
  const split = useMemo(() => {
    const free = [];
    const blocked = [];
    if (!timesValid) return { free, blocked };
    targetDates?.forEach((d) => {
      const c = findConflicts(existing, { workDate: d, startTime: `${startTime}:00`, endTime: computedEnd }, assignment?.id || null);
      if (c?.length) blocked?.push({ date: d, conflict: c?.[0] });
      else free?.push(d);
    });
    return { free, blocked };
  }, [targetDates, existing, startTime, computedEnd, timesValid, assignment?.id]);

  const projectLabel = (id) => {
    const p = projects?.find((x) => x?.id === id);
    if (!p) return '—';
    return p?.projectNumber ? `${p?.projectNumber} · ${p?.name}` : p?.name;
  };

  const workerName = workers?.find((w) => w?.id === workerId)?.full_name || '';

  // En mode « Un jour », le conflit bloque l'enregistrement et se dit en haut.
  const singleConflict = mode === 'single' && split?.blocked?.length ? split.blocked[0] : null;
  const singleConflictMessage = singleConflict
    ? t('teamSchedule.modal.conflict', {
        worker: workerName,
        project: projectLabel(singleConflict?.conflict?.projectId),
        time: `${shortTime(singleConflict?.conflict?.startTime)}-${shortTime(singleConflict?.conflict?.endTime)}`,
        date: formatDateWeekday(singleConflict?.date, language),
      })
    : '';

  const canSave =
    !!workerId && !!projectId && timesValid && split?.free?.length > 0 && !saving;

  // Echap : meme action que le bouton de fermeture de l'ecran affiche.
  useEscapeKey(() => { if (result) onSaved?.(); else onClose?.(); }, !!isOpen && !saving);

  if (!isOpen) return null;

  /**
   * Traduit une erreur d'ecriture. Sur un 23P01 on RELIT la journee fautive pour
   * pouvoir nommer le conflit — un aller-retour de plus, mais seulement dans le
   * chemin d'erreur, et c'est ce qui distingue un message utile d'un mur.
   */
  const describe = async (err, dateISO) => {
    let ctx = {};
    if (err?.code === OVERLAP_ERROR_CODE && workerId && dateISO) {
      const rows = await listWorkerAssignments(workerId, dateISO, dateISO);
      const c = findConflicts(rows, { workDate: dateISO, startTime: `${startTime}:00`, endTime: computedEnd }, assignment?.id || null)?.[0];
      if (c) {
        ctx = {
          conflict: c,
          worker: workerName,
          time: `${shortTime(c?.startTime)}-${shortTime(c?.endTime)}`,
          projectLabel: projectLabel(c?.projectId),
          dateLabel: formatDateWeekday(dateISO, language),
        };
      }
    }
    return assignmentErrorMessage(err, t, ctx);
  };

  const handleSave = async () => {
    setError('');
    if (!canSave) return;
    setSaving(true);

    if (isEdit) {
      const { error: saveError } = await updateAssignment(assignment?.id, {
        workerId, projectId, workDate: split?.free?.[0], startTime: `${startTime}:00`, endTime: computedEnd, note,
      });
      setSaving(false);
      if (saveError) {
        setError(await describe(saveError, split?.free?.[0]));
        return;
      }
      onSaved?.();
      return;
    }

    const payloads = split?.free?.map((d) => ({
      workerId, projectId, workDate: d, startTime: `${startTime}:00`, endTime: computedEnd, note,
    }));
    const { data, error: saveError } = await createAssignments(payloads);
    setSaving(false);

    if (saveError) {
      // Filet de securite : la contrainte EXCLUDE (23P01) a refuse le lot parce
      // qu'une autre personne a saisi entretemps. Jamais l'erreur brute a l'ecran.
      setError(await describe(saveError, split?.free?.[0]));
      return;
    }

    // Une serie rend compte de ce qui a saute ; un jour seul se ferme.
    if (mode === 'range' && split?.blocked?.length) {
      setResult({ created: data?.length || 0, blocked: split.blocked });
      return;
    }
    onSaved?.();
  };

  const handleDelete = async () => {
    setError('');
    setSaving(true);
    const { deleted, error: delError } = await deleteAssignment(assignment?.id);
    setSaving(false);
    if (delError || deleted === 0) {
      setError(delError ? await describe(delError, assignment?.workDate) : t('teamSchedule.errors.deleteFailed'));
      return;
    }
    onDeleted?.(assignment?.id);
  };

  const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#2C3E50] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
  const segClass = (active) => `px-2 py-2 rounded-lg border text-[11px] font-medium leading-tight transition-colors ${
    active ? 'bg-orange-50 border-primary text-primary' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
  }`;

  // ── Ecran de resultat d'une serie partiellement creee ───────────────────────
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
              <Icon name="Users" size={18} className="text-teal-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">{t('teamSchedule.result.title')}</h2>
            </div>
          </div>
            <button type="button" onClick={() => onSaved?.()} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
              <Icon name="CheckCircle" size={16} className="flex-shrink-0 text-green-600" />
              {t('teamSchedule.result.created', { count: result?.created })}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-2 leading-relaxed">
                {t('teamSchedule.result.skipped', { count: result?.blocked?.length, worker: workerName })}
              </p>
              <div className="space-y-1">
                {result?.blocked?.map((b) => (
                  <div key={b?.date} className="flex items-baseline gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <span className="font-medium text-amber-900 whitespace-nowrap tabular-nums">
                      {formatDate(b?.date, language)}
                    </span>
                    <span className="text-amber-700 leading-relaxed">
                      {t('teamSchedule.result.alreadyAt', {
                        time: `${shortTime(b?.conflict?.startTime)}-${shortTime(b?.conflict?.endTime)}`,
                        project: projectLabel(b?.conflict?.projectId),
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
            <button
              type="button"
              onClick={() => onSaved?.()}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* En-tete */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
              <Icon name="Users" size={18} className="text-teal-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">{isEdit ? t('teamSchedule.modal.editTitle') : t('teamSchedule.modal.createTitle')}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Le conflit passe avant les champs : c'est ce qui bloque l'enregistrement. */}
          {singleConflictMessage && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <Icon name="AlertTriangle" size={14} className="flex-shrink-0 mt-0.5 text-red-500" />
              <span className="leading-relaxed">{singleConflictMessage}</span>
            </div>
          )}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {/* Travailleur */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('teamSchedule.modal.worker')} <span className="text-red-500">*</span>
            </label>
            <GroupedSelect
              groups={workerGroups}
              value={workerId || null}
              onChange={(v) => setWorkerId(v || '')}
              placeholder={t('teamSchedule.modal.workerPlaceholder')}
              size="sm"
            />
          </div>

          {/* Projet */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('teamSchedule.modal.project')} <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={projectOptions}
              value={projectId || null}
              onChange={(v) => setProjectId(v || '')}
              placeholder={t('teamSchedule.modal.projectPlaceholder')}
              size="sm"
            />
          </div>

          {/* Quand — un jour, ou une serie. Absent en modification : on ne
              transforme pas une assignation existante en serie. */}
          {!isEdit && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('teamSchedule.modal.when')}</label>
              <div className="grid grid-cols-2 gap-1.5">
                <button type="button" onClick={() => setMode('single')} className={segClass(mode === 'single')}>
                  {t('teamSchedule.modal.singleDay')}
                </button>
                <button type="button" onClick={() => setMode('range')} className={segClass(mode === 'range')}>
                  {t('teamSchedule.modal.multipleDays')}
                </button>
              </div>
            </div>
          )}

          {/* Date, ou plage */}
          {(mode === 'single' || isEdit) ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('teamSchedule.modal.date')} <span className="text-red-500">*</span>
              </label>
              <input type="date" value={workDate} onChange={(e) => setWorkDate(e?.target?.value)} className={inputClass} />
              {workDate && (
                // Aucune classe CSS capitalize : elle mettrait une majuscule a CHAQUE
                // mot et rendrait « lundi 14 Septembre 2026 ».
                <p className="text-xs text-gray-500 mt-1 first-letter:uppercase">{formatDateWeekday(workDate, language)}</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('teamSchedule.modal.fromDate')} <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e?.target?.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('teamSchedule.modal.toDate')} <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e?.target?.value)} className={inputClass} />
                </div>
              </div>
              {fromDate && toDate && toDate >= fromDate && (
                <p className="text-xs text-gray-500 first-letter:uppercase">
                  {formatDateWeekday(fromDate, language)} → {formatDateWeekday(toDate, language)}
                </p>
              )}

              {/* Les sept jours. Fin de semaine decochee par defaut. */}
              <div>
                <div className="flex items-center gap-1.5">
                  {WEEKDAY_ORDER?.map((d) => {
                    const on = !!weekdays?.[d];
                    const isWeekendDay = d === 0 || d === 6;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setWeekdays((prev) => ({ ...prev, [d]: !prev?.[d] }))}
                        className={`w-8 h-8 rounded-full text-[11px] font-semibold transition-colors border ${
                          on
                            ? 'bg-primary border-primary text-white'
                            : `bg-white border-gray-200 ${isWeekendDay ? 'text-gray-300' : 'text-gray-500'} hover:border-gray-300`
                        }`}
                      >
                        {t(`teamSchedule.weekdays.${d}`)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">{t('teamSchedule.modal.weekendHint')}</p>
              </div>
            </div>
          )}

          {/* Heure de debut */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('teamSchedule.modal.startTime')} <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e?.target?.value)}
              className={`${inputClass} tabular-nums`}
              style={{ width: '130px' }}
            />
          </div>

          {/* Duree — trois segments. 4 h et 8 h calculent la fin. */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('teamSchedule.modal.duration')}</label>
            <div className="grid grid-cols-3 gap-1.5">
              {DURATIONS?.map((d) => (
                <button key={d?.key} type="button" onClick={() => setDuration(d?.key)} className={segClass(duration === d?.key)}>
                  {t(`teamSchedule.durations.${d?.key}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Fin — revelee par la seule duree libre */}
          {duration === 'custom' && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('teamSchedule.modal.from')}</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e?.target?.value)} className={`${inputClass} tabular-nums`} style={{ width: '120px' }} />
              </div>
              <span className="text-gray-400 pb-2.5">→</span>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('teamSchedule.modal.to')}</label>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e?.target?.value)} className={`${inputClass} tabular-nums`} style={{ width: '120px' }} />
              </div>
            </div>
          )}

          {/* La ligne derivee : ce qui sera reellement enregistre */}
          {timesValid ? (
            <div className="flex items-center gap-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
              <Icon name={mode === 'range' && !isEdit ? 'CalendarDays' : 'Clock'} size={14} className="flex-shrink-0 text-green-600" />
              <span className="tabular-nums font-medium">
                {mode === 'range' && !isEdit
                  ? t('teamSchedule.modal.summaryRange', {
                      count: targetDates?.length,
                      start: shortTime(`${startTime}:00`),
                      end: shortTime(computedEnd),
                    })
                  : `${shortTime(`${startTime}:00`)} → ${shortTime(computedEnd)} · ${durationLabel(spanMinutes)}`}
              </span>
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {t('teamSchedule.errors.endBeforeStart')}
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('teamSchedule.modal.note')}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              rows={3}
              placeholder={t('teamSchedule.modal.notePlaceholder')}
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>

        {/* Pied — Supprimer a gauche, Annuler + Enregistrer a droite */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <div>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="px-3.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Check" size={13} />}
              {/* Jamais un « Enregistrer » muet quand on ecrit dix lignes. */}
              {isEdit || split?.free?.length <= 1
                ? t('common.save')
                : t('teamSchedule.modal.createCount', { count: split?.free?.length })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;
